import React, { useEffect, useState } from "react";
import { SurveyDto } from "../../models/surveyDto";
import { Button, Container, Segment } from "semantic-ui-react";
import { QuestionDto } from "../../models/questionDto";
import { toast } from "react-toastify";
import SurveyItemViewer from "./surveyItemViewer";
import { useTranslation } from "react-i18next";
import { useStore } from "../../stores/store";
import { SurveySectionDto } from "../../models/surveySectionDto";

interface Props {
  survey: SurveyDto;
  userSurveyId: number;
  onEndSurvey: (s: SurveyDto) => void;
}

export default function SurveyPlayer({ survey, userSurveyId, onEndSurvey }: Props) {
  const { t } = useTranslation("common");
  const [saving, setSaving] = useState<boolean>(false);
  const [totalQuestionCount, setTotalQuestionCount] = useState<number>(0);
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] = useState<QuestionDto>();
  const [currentSection, setCurrentSection] = useState<SurveySectionDto>();
  const [currentSurvey, setCurrentSurvey] = useState<SurveyDto>({ ...survey });
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const { commonStore } = useStore();

  useEffect(() => {
    if (!survey || !survey.sections) {
      toast.error("Invalid survey ...");
      return;
    }

    let orderIndex = 0;
    let items: QuestionDto[] = [];
    if (survey.preSurveyQuestion != null && survey.preSurveyQuestionId != null)
      items.push({ ...survey.preSurveyQuestion, order: orderIndex++, isQuestion: true });
    survey.sections.forEach((section) => {
      //start adding questions
      section.questions.forEach((q) => {
        let questionItem: QuestionDto = {
          ...q,
          order: orderIndex++,
          isQuestion: true,
          id: q.id,
          sectionId: section.id,
        };
        items.push(questionItem);
      });
    });
    if (survey.postSurveyQuestion != null && survey.postSurveyQuestionId != null)
      items.push({ ...survey.postSurveyQuestion, order: orderIndex++, isQuestion: true });

    setTotalQuestionCount(items.filter((q) => q.isQuestion === true && q.weighted).length);
    setQuestions(items);
    setCurrentQuestion(items[0]);
    setCurrentSection(survey.sections[0]);
  }, []);

  const nextQuestion = () => {
    let index = currentItemIndex + 1;
    setSaving(true);
    if (index === questions.length) {
      onEndSurvey(currentSurvey);
    } else {
      setCurrentItemIndex(index);
      let question = questions[index];
      setCurrentQuestion(question);

      let section: SurveySectionDto | undefined = survey.sections?.find(
        (s) => s.id === question.sectionId
      );
      if (section) setCurrentSection(section);

      if (questions[index].isQuestion) setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
    setSaving(false);
  };

  const previousQuestion = () => {
    let index = currentItemIndex - 1;
    setCurrentItemIndex(index);
    let question = questions[index];
    setCurrentQuestion(question);

    let section: SurveySectionDto | undefined = survey.sections?.find(
      (s) => s.id === question.sectionId
    );
    if (section) setCurrentSection(section);

    if (questions[index].isQuestion) setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const setAnswer = (questionId: number, selectedValue: number | undefined | string) => {
    //update navigation questions
    let items = questions.map((obj) => {
      if (obj.id === questionId) {
        return { ...obj, selectedChoice: selectedValue === undefined ? null : selectedValue };
      }
      return obj;
    });
    if (items != null) setQuestions(items);

    //update survey object to be submitted
    let updatedQuestions = currentSurvey.questions.map((obj) => {
      if (obj.id === questionId) {
        return { ...obj, selectedChoice: selectedValue === undefined ? null : selectedValue };
      }
      return obj;
    });
    if (updatedQuestions != null) setCurrentSurvey({ ...currentSurvey, questions: updatedQuestions });

    //check if pre or post survey questions
    if (
      currentSurvey.preSurveyQuestion &&
      questionId === currentSurvey.preSurveyQuestionId &&
      selectedValue
    ) {
      setCurrentSurvey({
        ...currentSurvey,
        preSurveyQuestion: { ...currentSurvey.preSurveyQuestion, selectedChoice: selectedValue },
      });
    }
    if (
      currentSurvey.postSurveyQuestion &&
      questionId === currentSurvey.postSurveyQuestionId &&
      selectedValue
    )
      setCurrentSurvey({
        ...currentSurvey,
        postSurveyQuestion: { ...currentSurvey.postSurveyQuestion, selectedChoice: selectedValue },
      });
  };

  const navigationProgress = (
    <Container className="progressbar-container">
      <ul className="progressbar">
        {survey &&
          survey.sections &&
          survey.sections.map((sec) => (
            <li
              key={sec.id}
              className={
                currentSection && currentSection.sectionOrder > sec.sectionOrder
                  ? "active"
                  : currentSection && currentSection.sectionOrder === sec.sectionOrder
                  ? "current"
                  : ""
              }
            >
              <div className="step-title">{commonStore.getSurveySectionTitle(sec)}</div>
            </li>
          ))}
      </ul>
    </Container>
  );

  const sectionSummary = (
    <>
      {<Segment className="area-title">{commonStore.getSurveySectionTitle(currentSection)}</Segment>}
      <Segment className="survey-question">
        {commonStore.getSurveySectionDescription(currentSection)}
      </Segment>
    </>
  );

  let content = (
    <>
      {currentQuestion?.weighted && navigationProgress}
      {currentQuestion?.weighted && currentSection && sectionSummary}
      <Segment className="question-count">
        {currentQuestion?.weighted && (
          <span className="question-count">
            {currentQuestionIndex}/{totalQuestionCount}
          </span>
        )}
      </Segment>
      <Segment className="lpoasis-survey">
        {questions && questions.length > 0 && currentQuestion && currentSection && (
          <SurveyItemViewer
            isQuestion={currentQuestion.isQuestion}
            question={currentQuestion}
            section={currentSection}
            onAnswerchange={(questionId, value: any) => {
              setCurrentQuestion({ ...currentQuestion, selectedChoice: value });
              setAnswer(questionId, value);
            }}
          />
        )}

        <Container className="survey-btns">
          <Segment>
            {currentItemIndex > 0 && (
              <Button
                primary
                disabled={saving}
                floated="left"
                onClick={() => {
                  previousQuestion();
                }}
              >
                {t("Common.Previous")}
              </Button>
            )}
            <Button
              primary
              loading={saving}
              floated="right"
              disabled={
                currentQuestion?.isQuestion &&
                (currentQuestion.selectedChoice === 0 ||
                  currentQuestion.selectedChoice == null ||
                  currentQuestion.selectedChoice == undefined)
              }
              onClick={() => {
                nextQuestion();
              }}
            >
              {t("Common.Next")}
            </Button>
          </Segment>
        </Container>
      </Segment>
    </>
  );

  return <>{currentSurvey && content}</>;
}
